import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "../../lib/utils";

/**
 * Menu suspenso simples, sem dependencia nova: gatilho + painel ancorado.
 * Fecha ao clicar fora, ao pressionar Escape e ao selecionar um item.
 */
export function DropdownMenu({ trigger, children, align = "end", className = "", panelClassName = "" }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const panelId = useId();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        close();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, close]);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {React.cloneElement(trigger, {
        onClick: (event) => {
          trigger.props.onClick?.(event);
          if (!event.defaultPrevented) {
            setOpen((previous) => !previous);
          }
        },
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": open ? panelId : undefined,
        "data-open": open ? "" : undefined,
      })}

      {open && (
        <div
          id={panelId}
          role="menu"
          onClick={close}
          className={cn(
            "absolute top-[calc(100%+8px)] z-50 min-w-[252px] rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-xl",
            "animate-in fade-in slide-in-from-top-1 duration-150",
            align === "end" ? "right-0" : "left-0",
            panelClassName
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function DropdownMenuLabel({ className = "", ...props }) {
  return <div className={cn("mb-1.5 border-b border-border px-2 pb-3 pt-2", className)} {...props} />;
}

export function DropdownMenuItem({ className = "", tone = "default", ...props }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        "flex min-h-[39px] w-full items-center gap-2.5 rounded-lg px-2.5 text-xs font-semibold transition-colors",
        "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:bg-accent",
        "[&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0",
        tone === "danger" ? "text-destructive hover:text-destructive" : "text-muted-foreground",
        className
      )}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({ className = "", ...props }) {
  return <div className={cn("my-1.5 h-px bg-border", className)} {...props} />;
}

export default DropdownMenu;
